import React from 'react';
import { DateTime } from 'luxon';
import {
  StyleSheet,
  View,
  ScrollView,
  Text,
  TouchableHighlight,
} from './components/base_components';
import { BusyComponent, BusyOverlay } from './components/busy_component';

import BreadcrumbPath from './components/breadcrumb_path';
import Checkbox from './components/checkbox';
import Header from './components/header';
import Link from './components/link';
import MediaIcon from './components/media_icon';
import MediaOverlay from './components/overlays/media_overlay';
import ProgressSpinner from './components/progress_spinner';
import TextButton from './components/buttons/text_button';

import UserStore from './stores/user_store';
import MediaStore from './stores/media_store';
import VolumeStore from './stores/volume_store';

const LOGO_IMG = require('./assets/img/logo.png');

const styles = StyleSheet.create({
  volumeMediaScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  scroller: {
    flex: 1,
    alignSelf: 'stretch',
    flexDirection: 'column',
  },
  scrollInner: {
    paddingLeft: '3em',
    paddingRight: '3em',
    paddingTop: '1em',
    paddingBottom: '5em',
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflowY: 'scroll',
  },
  list: {
    marginTop: '1em',
    marginBottom: '1em',
  },
  add: {
    marginTop: '1em',
  },
  mediaList: {
    marginTop: '0.5em',
    marginBottom: '1em',
    alignSelf: 'stretch',
    flexDirection: 'column',
  },
  dirEntry: {
    flexDirection: 'column',
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    ':hover': {
      backgroundColor: 'lightblue',
    },
    backgroundColor: '#f1f1f1',
  },
  icon: {
    padding: '0.3em',
    width: '1.5em',
    height: '1.5em',
    marginRight: '0.8em',
    backgroundColor: '#ccc',
    borderRadius: '0.3em',
  },
  dirLink: {
    alignSelf: 'stretch',
    padding: '0.4em',
    borderRadius: '0.5em',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  mediaText: {},
  mediaName: {
    width: '40em',
  },
  size: {
    marginLeft: '1em',
    width: '8em',
  },
  deletedText: {
    color: 'red',
  },
  topView: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    marginLeft: '0.5em',
  },
  volumeText: {
    marginTop: '0.8em',
    fontSize: '1.2em',
    fontWeight: 'bold',
  },
  pathText: {
    marginTop: '0.5em',
    fontSize: '1.2em',
    fontWeight: 'bold',
  },
  volumeSwitch: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spacer: {
    flex: 1,
  },
  checkboxText: {
    marginLeft: '0.5em',
  },
  odd: {
    backgroundColor: 'transparent',
  },
  statList: {
    marginTop: '0.5em',
    height: '1.5em',
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stat: {
    marginRight: '1em',
  },
  statText: {
    marginRight: '0.3em',
    fontWeight: 'bold',
  },
  statValue: {},
  undeleteButton: {
    height: '1.5em',
  },
  undeleteButtonText: {
    fontSize: '1em',
  },
  date: {
    marginLeft: '1em',
    width: '12em',
  },
  last_user: {
    marginLeft: '1em',
    width: '10em',
  },
});

type Props = {};
type State = {
  is_admin: boolean;
  source_key: string;
  source_dir: [any] | null;
  media_detail: any | null;
  show_deleted: boolean;
};

export default class VolumeMediaScreen extends BusyComponent<Props, State> {
  state = {
    is_admin: UserStore.getUser().is_admin,
    source_key: '',
    source_dir: null,
    media_detail: null,
    show_deleted: false,
  };
  componentDidMount() {
    const volume_id = this._getVolumeId();
    const path = this._getPath();
    MediaStore.addChangeListener(this._onMediaUpdate);
    VolumeStore.addChangeListener(this._onVolumeUpdate);
    VolumeStore.fetch();
    MediaStore.fetchVolumeDir({ volume_id, path });
    this._onMediaUpdate();
    this._onVolumeUpdate();
  }
  componentWillUnmount() {
    VolumeStore.removeChangeListener(this._onVolumeUpdate);
    MediaStore.removeChangeListener(this._onMediaUpdate);
  }
  _onVolumeUpdate = () => {
    this.forceUpdate();
  };
  _onMediaUpdate = () => {
    const volume_id = this._getVolumeId();
    const path = this._getPath();
    const source_dir = MediaStore.getVolumeDirList(volume_id, path);
    const source_key = `${volume_id}-${path}`;
    this.setState({ source_dir, source_key });
  };
  _getVolumeId() {
    const volume_id = parseInt(this.props.match.params.volume_id) || 0;
    return volume_id;
  }
  _getPath() {
    const source_path = this.props.match.params.path;
    const path = '/' + (source_path ? source_path + '/' : '');
    return path;
  }
  _onEntryPress = (entry) => this.setState({ media_detail: entry.media });
  _onEntryClose = () => this.setState({ media_detail: null });
  _onShowDeletedChange = (show_deleted) => this.setState({ show_deleted });
  _onMediaRefresh = () => {
    const volume_id = this._getVolumeId();
    const path = this._getPath();
    MediaStore.fetchVolumeDir({ volume_id, path });
    this.setState({ source_dir: null });
  };
  _undeleteAllPress = () => {
    const volume_id = this._getVolumeId();
    const { source_dir } = this.state;
    const media_list = source_dir.media_list.filter(
      (media) => media.is_deleted
    );
    const count = media_list.length;
    const confirmed = window.confirm(`Undelete ${count} files?`);
    if (count && confirmed && this.setBusy()) {
      const opts = { volume_id, media_list, on_progress: this._onProgress };
      MediaStore.undeleteList(opts, (err) => {
        this.clearBusy();
        if (err) {
          window.alert('Undelete failed');
        }
        this._onMediaRefresh();
      });
    }
  };
  _onProgress = () => {};

  render() {
    const {
      is_admin,
      source_key,
      source_dir,
      media_detail,
      show_deleted,
    } = this.state;

    const volume_id = this._getVolumeId();
    const path = this._getPath();
    const volume = VolumeStore.getVolume(volume_id);
    const key = `${volume_id}-${path}`;

    let media = null;
    let stats = null;
    if (key !== source_key) {
      MediaStore.fetchVolumeDir({ volume_id, path });
      media = <ProgressSpinner />;
    } else if (!source_dir) {
      media = <ProgressSpinner />;
    } else {
      const media_list = _filterList(source_dir, path, volume_id, show_deleted);
      if (media_list.length > 0) {
        media = media_list.map((m, i) => (
          <DirEntry
            key={i}
            odd={i % 2 === 1}
            entry={m}
            onPress={this._onEntryPress}
          />
        ));
      } else {
        media = <Text style={styles.noMedia}>Empty Directory</Text>;
      }
      const deleted_count = source_dir.media_list.reduce(
        (memo, media) => memo + (media.is_deleted ? 1 : 0),
        0
      );
      const file_count = source_dir.media_list.length - deleted_count;
      const dir_count = source_dir.dir_list.length;
      let undelete_all = null;
      if (deleted_count && show_deleted) {
        undelete_all = (
          <TextButton
            style={styles.undeleteButton}
            textStyle={styles.undeleteButtonText}
            text='Undelete All'
            onPress={this._undeleteAllPress}
          />
        );
      }

      stats = (
        <View style={styles.statList}>
          <Stat text='Files' value={file_count} />
          <Stat text='Directories' value={dir_count} />
          <Stat text='Deleted Files' value={deleted_count} />
          <View style={styles.spacer} />
          {undelete_all}
        </View>
      );
    }

    let overlay = null;
    if (media_detail) {
      overlay = (
        <MediaOverlay
          media={media_detail}
          volumeId={volume_id}
          onMediaRefresh={this._onMediaRefresh}
          onClose={this._onEntryClose}
        />
      );
    }
    let volume_name = '';
    if (volume) {
      volume_name = volume.volume_name;
    }

    return (
      <View style={styles.volumeMediaScreen}>
        <ScrollView
          style={styles.scroller}
          contentContainerStyle={styles.scrollInner}
        >
          <Header />
          <View style={styles.topView}>
            <View style={styles.volumeSwitch}>
              <Text style={styles.volumeText}>Volume: {volume_name}</Text>
              <View style={styles.spacer} />
              <Checkbox
                style={styles.checkbox}
                textStyle={styles.checkboxText}
                text='Show Deleted'
                value={show_deleted}
                onChange={this._onShowDeletedChange}
              />
            </View>
            <BreadcrumbPath
              style={styles.pathText}
              volumeName={volume_name}
              prefix={`/volume/${volume_id}`}
              path={path}
            />
            {stats}
          </View>
          <View style={styles.mediaList}>{media}</View>
        </ScrollView>
        {overlay}
        <BusyOverlay isBusy={this.state.is_busy} />
      </View>
    );
  }
}

function Stat(props) {
  return (
    <View style={styles.stat}>
      <Text style={styles.statText}>{props.text + ':'}</Text>
      <Text style={styles.statValue}>{String(props.value)}</Text>
    </View>
  );
}
function DirEntry(props) {
  const { entry, odd, onPress } = props;
  const { is_dir, path, name, media, is_deleted } = entry;

  let extra_row = odd ? styles.odd : null;
  let extra_text = is_deleted ? styles.deletedText : null;
  let content = null;
  if (is_dir) {
    content = (
      <Link style={styles.dirLink} href={path}>
        <MediaIcon style={styles.icon} isDir={is_dir} name={name} />
        <Text style={[styles.mediaText, extra_text]}>{name}</Text>
      </Link>
    );
  } else {
    const { file_size, last_updated, last_update_user_id } = media;
    const _onPress = () => {
      onPress(entry);
    };
    content = (
      <TouchableHighlight style={styles.dirLink} onPress={_onPress}>
        <MediaIcon style={styles.icon} isDir={is_dir} name={name} />
        <Text style={[styles.mediaName, extra_text]} title={name}>
          {name}
        </Text>
        <Text style={styles.size}>{_bytes(file_size)}</Text>
        <Text style={styles.date}>{_date(last_updated)}</Text>
        <Text style={styles.last_user}>{last_update_user_id}</Text>
      </TouchableHighlight>
    );
  }

  return <View style={[styles.dirEntry, extra_row]}>{content}</View>;
}

function _filterList(source_dir, path, volume_id, show_deleted) {
  const low_path = path.toLowerCase();
  const output_list = [];
  if (source_dir) {
    const { media_list, dir_list } = source_dir;

    media_list.forEach((media) => {
      const { media_path, is_deleted } = media;
      const show = show_deleted || !is_deleted;
      if (media_path.toLowerCase().indexOf(low_path) === 0 && show) {
        const name = media_path.slice(path.length);
        output_list.push({
          name,
          is_dir: false,
          media,
          is_deleted,
        });
      }
    });
    dir_list.forEach((dir) => {
      const { dir_name, file_count } = dir;
      const is_deleted = file_count === 0;
      const show = show_deleted || !is_deleted;
      if (show) {
        const name = dir_name.slice(path.length);
        output_list.push({
          name,
          is_dir: true,
          media: null,
          is_deleted,
          path: `/volume/${volume_id}${path}${name}`,
        });
      }
    });
  }
  output_list.sort(_outputSort);
  return output_list;
}

const LOCALE_OPTS = { sensitivity: 'accent' };
function _outputSort(a, b) {
  const name_diff = a.name.localeCompare(b.name, undefined, LOCALE_OPTS);
  if (a.is_dir && b.is_dir) {
    return name_diff;
  } else if (a.is_dir) {
    return -1;
  } else if (b.is_dir) {
    return 1;
  } else {
    return name_diff;
  }
}
const KB = 1024;
const MB = 1024 * KB;
const GB = 1024 * MB;

function _bytes(number) {
  let ret = number;
  if (number > GB) {
    ret = (number / GB).toFixed(3) + 'GB';
  } else if (number > MB) {
    ret = (number / MB).toFixed(3) + 'MB';
  } else if (number > KB) {
    ret = (number / KB).toFixed(3) + 'KB';
  } else {
    ret = number + 'B';
  }
  return ret;
}
function _date(date_s: string): string {
  let ret = '';
  const dt = DateTime.fromISO(date_s);
  if (dt.isValid) {
    ret = dt.toFormat('yyyy-MM-dd HH:mm:ss');
  }
  return ret;
}
